import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Cloud, Car, Trash2, Zap, Recycle, TrendingUp, TrendingDown, Info } from 'lucide-react';
import type { ImpactKPI } from '@/hooks/useImpactData';
import { IMPACT_METRIC_COLORS } from '@/utils/impactMetricColors';

interface ImpactKPICardsProps {
  kpis: ImpactKPI[];
}

const KPI_ICONS: Record<string, React.ElementType> = {
  cloud: Cloud,
  car: Car,
  trash: Trash2,
  zap: Zap,
  recycle: Recycle,
};

// Usa cores centralizadas para consistência com gráficos
const KPI_COLORS: Record<string, string> = {
  co2: IMPACT_METRIC_COLORS.co2,
  km: IMPACT_METRIC_COLORS.km,
  landfill: IMPACT_METRIC_COLORS.landfill,
  energy: IMPACT_METRIC_COLORS.energy,
  recovered: IMPACT_METRIC_COLORS.recovered,
};

const formatValue = (value: number, unit: string): string => {
  if (unit === 'm³') {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(value);
};

export const ImpactKPICards = ({ kpis }: ImpactKPICardsProps) => {
  // Filter to show only the main 5 KPIs, excluding 'fridges'
  const mainKpis = kpis.filter(kpi => ['co2', 'km', 'landfill', 'energy', 'recovered'].includes(kpi.id) && kpi.value > 0);

  // Layout: 3 cards on top row, 2 on bottom row
  const topRow = mainKpis.slice(0, 3);
  const bottomRow = mainKpis.slice(3, 5);

  return (
    <div className="space-y-4">
      {/* Top row - 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TooltipProvider>
          {topRow.map((kpi) => {
            const IconComponent = KPI_ICONS[kpi.icon] || Cloud;
            const color = KPI_COLORS[kpi.id] || '#10B981';

            return (
              <Card key={kpi.id} className="bg-white border-border hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-600">{kpi.label}</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-gray-500 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>{kpi.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-3">
                    <div 
                      className="p-2.5 rounded-lg"
                      style={{ backgroundColor: `${color}15` }}
                    >
                      <IconComponent 
                        className="h-5 w-5"
                        style={{ color }}
                      />
                    </div>
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span 
                          className="text-2xl font-bold"
                          style={{ color }}
                        >
                          {formatValue(kpi.value, kpi.unit)}
                        </span>
                        <span className="text-sm text-gray-600">{kpi.unit}</span>
                      </div>
                      {kpi.change !== undefined && (
                        <div className={`flex items-center gap-1 ${
                          kpi.change >= 0 ? 'text-emerald-600' : 'text-red-500'
                        }`}>
                          {kpi.change >= 0 ? (
                            <TrendingUp className="h-3 w-3" />
                          ) : (
                            <TrendingDown className="h-3 w-3" />
                          )}
                          <span className="text-xs font-medium">
                            {kpi.change >= 0 ? '+' : ''}{kpi.change}% vs período anterior
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TooltipProvider>
      </div>

      {/* Bottom row - 2 cards */}
      {bottomRow.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TooltipProvider>
            {bottomRow.map((kpi) => {
              const IconComponent = KPI_ICONS[kpi.icon] || Cloud;
              const color = KPI_COLORS[kpi.id] || '#10B981';

              return (
                <Card key={kpi.id} className="bg-white border-border hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-600">{kpi.label}</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-4 w-4 text-gray-500 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>{kpi.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-center gap-3">
                      <div 
                        className="p-2.5 rounded-lg"
                        style={{ backgroundColor: `${color}15` }}
                      >
                        <IconComponent 
                          className="h-5 w-5"
                          style={{ color }}
                        />
                      </div>
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span 
                            className="text-2xl font-bold"
                            style={{ color }}
                          >
                            {formatValue(kpi.value, kpi.unit)}
                          </span>
                          <span className="text-sm text-gray-600">{kpi.unit}</span>
                        </div>
                        {kpi.change !== undefined && (
                          <div className={`flex items-center gap-1 ${
                            kpi.change >= 0 ? 'text-emerald-600' : 'text-red-500'
                          }`}>
                            {kpi.change >= 0 ? (
                              <TrendingUp className="h-3 w-3" />
                            ) : (
                              <TrendingDown className="h-3 w-3" />
                            )}
                            <span className="text-xs font-medium">
                              {kpi.change >= 0 ? '+' : ''}{kpi.change}% vs período anterior
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TooltipProvider>
        </div>
      )}
    </div>
  );
};
